"use client";

import * as React from "react";
import { EditorContent, EditorContext, useEditor } from "@tiptap/react";

// --- Tiptap Core Extensions ---
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight"
import { TextStyle } from "@tiptap/extension-text-style"
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Selection } from "@tiptap/extensions";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import { YoutubeAlign } from "../../../components/tiptap-extensions/youtube-aligned";
import { Table as BaseTable } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table/row";
import { TableHeader } from "@tiptap/extension-table/header";
import { TableCell } from "@tiptap/extension-table/cell";
import imageLine from "../../../../assets/img/Image_Line.svg";

// --- UI Primitives ---
import { Button } from "../../../components/tiptap-ui-primitive/button";
import { Spacer } from "../../../components/tiptap-ui-primitive/spacer";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "../../../components/tiptap-ui-primitive/toolbar";

// --- Tiptap Node ---
import { ImageUploadNode } from "../../../components/tiptap-node/image-upload-node/image-upload-node-extension";
import { HorizontalRule } from "../../../components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import { FileAttachment } from "../../../components/tiptap-extensions/file-attachment";
import { ResizableImage } from "../../../components/tiptap-extensions/resizable-image";
import { AlignableImage } from "../../../components/tiptap-extensions/alignable-image";
import "../../../components/tiptap-node/blockquote-node/blockquote-node.scss";
import "../../../components/tiptap-node/code-block-node/code-block-node.scss";
import "../../../components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "../../../components/tiptap-node/list-node/list-node.scss";
import "../../../components/tiptap-node/image-node/image-node.scss";
import "../../../components/tiptap-node/heading-node/heading-node.scss";
import "../../../components/tiptap-node/paragraph-node/paragraph-node.scss";
import "../../../styles/resizable-image.scss";

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "../../../components/tiptap-ui/heading-dropdown-menu";
import { ImageUploadButton } from "../../../components/tiptap-ui/image-upload-button";
import { ListDropdownMenu } from "../../../components/tiptap-ui/list-dropdown-menu";
import { BlockquoteButton } from "../../../components/tiptap-ui/blockquote-button";
import { CodeBlockButton } from "../../../components/tiptap-ui/code-block-button";
import {
  ColorHighlightPopover,
  ColorHighlightPopoverContent,
  ColorHighlightPopoverButton,
} from "../../../components/tiptap-ui/color-highlight-popover"
import { ColorFontPopover, ColorFontPopoverButton, ColorFontPopoverContent } from "../../../components/tiptap-ui/color-font-popover/color-font-popover";
import { LinkContent } from "../../../components/tiptap-ui/link-popover";
import { LinkAddButton, LinkRemoveButton } from "../../../components/tiptap-ui/link-toolbar-buttons";
import { MarkButton } from "../../../components/tiptap-ui/mark-button";
import { TextAlignButton } from "../../../components/tiptap-ui/text-align-button";
import { UndoRedoButton } from "../../../components/tiptap-ui/undo-redo-button";

// --- Icons ---
import { ArrowLeftIcon } from "../../../components/tiptap-icons/arrow-left-icon";
import { HighlighterIcon } from "../../../components/tiptap-icons/highlighter-icon";
import { LinkIcon } from "../../../components/tiptap-icons/link-icon";

// --- Hooks ---
import { useIsMobile } from "../../../hooks/use-mobile";
import { useWindowSize } from "../../../hooks/use-window-size";
import { useCursorVisibility } from "../../../hooks/use-cursor-visibility";

// --- Components ---
import { ThemeToggle } from "../../../components/tiptap-templates/simple/theme-toggle";

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from "../../../lib/tiptap-utils";

// --- Styles ---
import "../../../components/tiptap-templates/simple/simple-editor.scss";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../tiptap-ui-primitive/dropdown-menu";
import { ChevronDownIcon } from "../../tiptap-icons/chevron-down-icon";
import { useIsNodeActive } from "../../../hooks/use-is-node-active";

import { AlignLeftIcon } from "../../../components/tiptap-icons/align-left-icon";
import { AlignCenterIcon } from "../../../components/tiptap-icons/align-center-icon";
import { AlignRightIcon } from "../../../components/tiptap-icons/align-right-icon";

import { Card, CardBody } from "../../../components/tiptap-ui-primitive/card";
import TableEdgeControls from "../../tiptap-extensions/table-edge-controls";
import {
  TABLE_PICKER_CELL,
  TABLE_PICKER_GAP,
  TABLE_PICKER_MAX_COLS,
  TABLE_PICKER_MAX_ROWS,
  useTableEditor,
} from "../../../hooks/use-table-editor";
import { BvIcon } from "bevi-icon";
import { useArticles } from "../../../../providers/Articles/ArticlesProvider";
import * as XLSX from "xlsx";
import { useRef } from "react";
import { ExcelTableNode } from "../../tiptap-extensions/excel-table-node";
import { InlineImage } from "../../tiptap-extensions/inline-image";
import { HtmlPreviewNode } from "../../tiptap-node/html-preview-node/html-preview-node-extension";
import { LoomVideo } from "../../tiptap-extensions/loom-video";
import { LoomAlign } from "../../tiptap-extensions/loom-align";
import { FreezeTableCommand } from "../../tiptap-extensions/table-freeze-dom";

const TextStyleWithColor = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => element.style.color || null,
        renderHTML: (attributes) => {
          if (!attributes.color) return {}
          return { style: `color: ${attributes.color}` }
        },
      },
    }
  },
})

export function createSlug(text: string) {
  return text
    .normalize("NFD") // tira acentos
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\w]+/g, "-") // troca qualquer coisa por "-"
    .replace(/^-+|-+$/g, ""); // remove "-" do começo/fim
}

export async function parseExcel(file: File) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];

  const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];

  const [columns, ...rows] = json;

  return {
    columns,
    rows,
  };
}

export function getLoomEmbedUrl(url: string): string | null {
  const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (!match) return null;

  return `https://www.loom.com/embed/${match[1]}`;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableBulkActions: {
      /** Seleciona a linha inteira (CellSelection). */
      selectTableRow: () => ReturnType;
      /** Seleciona a coluna inteira (CellSelection). */
      selectTableColumn: () => ReturnType;
      /** Limpa o conteúdo de todas as células da linha atual. */
      clearTableRow: () => ReturnType;
      /** Limpa o conteúdo de todas as células da coluna atual. */
      clearTableColumn: () => ReturnType;
    };
    fileAttachment: {
      /** Insere um card de arquivo (link para download). */
      insertFileAttachment: (attrs: {
        href: string;
        name?: string;
        mime?: string;
        size?: number;
      }) => ReturnType;
    };
  }
}

function setYoutubeSize(editor: any, width: number) {
  const attrs = editor.getAttributes("youtube") || {};
  const ratio =
    attrs?.height && attrs?.width && Number(attrs.width) > 0
      ? Number(attrs.height) / Number(attrs.width)
      : 9 / 16;
  const height = Math.round(width * ratio);
  editor.chain().focus().updateAttributes("youtube", { width, height }).run();
}

// const TableCellWithBg = TableCell.extend({
//   addAttributes() {
//     return {
//       ...this.parent?.(),
//       backgroundColor: {
//         default: null,
//         parseHTML: (el) =>
//           el.style.backgroundColor || el.getAttribute("data-bg") || null,
//         renderHTML: (attrs) => {
//           if (!attrs.backgroundColor) return {};
//           return {
//             "data-bg": attrs.backgroundColor,
//             style: `background-color: ${attrs.backgroundColor}`,
//           };
//         },
//       },
//     };
//   },
// });

export const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),

      freezeRow: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-freeze-row") === "true",
        renderHTML: (attrs) =>
          attrs.freezeRow ? { "data-freeze-row": "true" } : {},
      },

      freezeCol: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-freeze-col") === "true",
        renderHTML: (attrs) =>
          attrs.freezeCol ? { "data-freeze-col": "true" } : {},
      },
    };
  },
});

const TableCellWithBg = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),

      freezeCol: {
        default: false,
        parseHTML: (el) => el.getAttribute("data-freeze-col") === "true",
        renderHTML: (attrs) =>
          attrs.freezeCol ? { "data-freeze-col": "true" } : {},
      },

      freezeRow: {
        default: false,
        parseHTML: (el) => el.getAttribute("data-freeze-row") === "true",
        renderHTML: (attrs) =>
          attrs.freezeRow ? { "data-freeze-row": "true" } : {},
      },
    };
  },
});

const ResponsiveTable = BaseTable.extend({
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      { class: "table-responsive" },
      [
        "table",
        {
          ...HTMLAttributes,
          class: `table table-bordered ${HTMLAttributes.class ?? ""}`,
        },
        0,
      ],
    ];
  },
});

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  onColorClick,
  isMobile,
  editor,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  onColorClick: () => void;
  isMobile: boolean;
  editor: any;
}) => {
  const [ytMenuOpen, setYtMenuOpen] = React.useState(false);
  const [ytWidth, setYtWidth] = React.useState(640);
  const isYoutubeActive = useIsNodeActive(editor, "youtube");
  const ytDisabled = !isYoutubeActive;
  const isTableActive = useIsNodeActive(editor, "table");
  const { tbMenuOpen, setTbMenuOpen, hover, setHover, colorCurrentRow } =
    useTableEditor(editor, "table");
  const { setIsPreview, isPreview } = useArticles();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const imageFileRef = useRef<HTMLInputElement | null>(null);

  const syncWidthFromNode = React.useCallback(() => {
    if (!editor?.isActive("youtube")) return;
    const w = Number(editor.getAttributes("youtube")?.width ?? 640);
    setYtWidth(Number.isFinite(w) ? w : 640);
  }, [editor]);

  React.useEffect(() => {
    if (!tbMenuOpen) setHover({ rows: 0, cols: 0 });
  }, [tbMenuOpen]);

  const ROW_COLORS = [
    "#ffffff",
    "#f3f4f6",
    "#fef3c7",
    "#dcfce7",
    "#dbeafe",
    "#fae8ff",
    "#fee2e2",
    "#fde68a",
  ];

  const handlePreview = () => {
    setIsPreview((prev) => !prev);
  };

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const insertInlineImage = async (file: File) => {
    const base64 = await fileToBase64(file);

    editor
      ?.chain()
      .focus()
      .insertContent({
        type: "inlineImage",
        attrs: {
          src: base64,
          alt: file.name,
          width: "auto",
          height: "auto",
        },
      })
      .run();
  };

  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu
          types={["bulletList", "orderedList", "taskList"]}
          portal={isMobile}
          labels={{
            bulletList: "Lista com marcadores",
            orderedList: "Lista numerada",
            taskList: "Lista de tarefas",
            indent: "Aumentar recuo",
            outdent: "Diminuir recuo",
          }}
        />
        <BlockquoteButton />
        {/* <CodeBlockButton /> */}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? (
          <ColorFontPopover />
        ) : (
          <ColorFontPopoverButton onClick={onColorClick} />
        )}
        {/* {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />} */}

        <LinkAddButton editor={editor} />
        <LinkRemoveButton editor={editor} />

        <button
          className="tiptap-button"
          data-style="ghost"
          onClick={() => {
            if (!editor) return;

            const text = editor.state.doc.textBetween(
              editor.state.selection.from,
              editor.state.selection.to,
            );

            if (!text) {
              alert("Selecione um texto para criar a âncora.");
              return;
            }

            const slug = createSlug(text);

            editor
              .chain()
              .focus()
              .extendMarkRange("link")
              .setLink({
                href: `#${slug}`,
                target: null,
                rel: null,
              })
              .run();
          }}
        >
          <i className="fa-solid fa-anchor"></i>
        </button>
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <DropdownMenu open={tbMenuOpen} onOpenChange={setTbMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              data-style="ghost"
              aria-label="Tabela"
              title="Tabela"
            >
              <i className="fa-solid fa-table"></i>
              <ChevronDownIcon className="tiptap-button-dropdown-small" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" portal={isMobile}>
            <Card>
              <CardBody>
                {/* --- Seletor de tamanho (quadradinhos) --- */}
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {hover.rows > 0 && hover.cols > 0
                      ? `${hover.cols} × ${hover.rows}`
                      : "Escolha o tamanho"}
                  </div>

                  <div
                    role="grid"
                    onMouseLeave={() => setHover({ rows: 0, cols: 0 })}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${TABLE_PICKER_MAX_COLS}, ${TABLE_PICKER_CELL}px)`,
                      gridAutoRows: `${TABLE_PICKER_CELL}px`,
                      gap: TABLE_PICKER_GAP,
                      padding: 4,
                    }}
                  >
                    {Array.from({
                      length: TABLE_PICKER_MAX_ROWS * TABLE_PICKER_MAX_COLS,
                    }).map((_, i) => {
                      const r = Math.floor(i / TABLE_PICKER_MAX_COLS);
                      const c = i % TABLE_PICKER_MAX_COLS;
                      const active = r < hover.rows && c < hover.cols;
                      return (
                        <button
                          key={i}
                          type="button"
                          role="gridcell"
                          aria-selected={active}
                          aria-label={`${c + 1} col × ${r + 1} lin`}
                          onMouseEnter={() =>
                            setHover({ rows: r + 1, cols: c + 1 })
                          }
                          onClick={() => {
                            editor
                              ?.chain()
                              .focus()
                              .insertTable({
                                rows: r + 1,
                                cols: c + 1,
                                withHeaderRow: true,
                              })
                              .run();
                            setTbMenuOpen(false);
                          }}
                          style={{
                            width: TABLE_PICKER_CELL,
                            height: TABLE_PICKER_CELL,
                            padding: 0,
                            borderRadius: 4,
                            border: "1px solid #e5e7eb",
                            background: active ? "rgba(0,0,0,.12)" : "white",
                            outline: "none",
                            cursor: "pointer",
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* --- Separador visual --- */}
                <div style={{ height: 8 }} />

                {/* --- Ações de LINHA --- */}
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Linha</div>

                  {/* Paleta de cores da linha atual */}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {ROW_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        disabled={!editor?.isActive("table")}
                        title={`Cor ${color}`}
                        onClick={() => {
                          colorCurrentRow(editor!, color);
                        }}
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          border: "1px solid #e5e7eb",
                          background: color,
                          cursor: editor?.isActive("table")
                            ? "pointer"
                            : "not-allowed",
                        }}
                      />
                    ))}
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => {
                        colorCurrentRow(editor!, null);
                      }}
                    >
                      Limpar cor
                    </Button>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => {
                        editor?.chain().focus().addRowBefore().run();
                      }}
                    >
                      + linha antes
                    </Button>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => {
                        editor?.chain().focus().addRowAfter().run();
                      }}
                    >
                      + linha depois
                    </Button>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => {
                        editor?.chain().focus().deleteRow().run();
                      }}
                    >
                      - remover linha
                    </Button>
                  </div>
                </div>

                {/* --- Separador visual --- */}
                <div style={{ height: 8 }} />

                {/* --- Ações de COLUNA --- */}
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Coluna</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() =>
                        editor?.chain().focus().addColumnBefore().run()
                      }
                    >
                      + coluna antes
                    </Button>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() =>
                        editor?.chain().focus().addColumnAfter().run()
                      }
                    >
                      + coluna depois
                    </Button>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() =>
                        editor?.chain().focus().deleteColumn().run()
                      }
                    >
                      - remover coluna
                    </Button>
                  </div>
                </div>

                {/* --- Ações de fixar --- */}
                <div style={{ display: "grid", gap: 8, marginTop: "10px" }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Fixar</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => editor?.commands.freezeColumn()}
                    >
                      + fixar coluna
                    </Button>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => editor?.commands.freezeRow()}
                    >
                      + fixar linha
                    </Button>
                    <Button
                      data-style="ghost"
                      disabled={!editor?.isActive("table")}
                      onClick={() => editor?.commands.clearFreeze()}
                    >
                      - remover
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </DropdownMenuContent>
        </DropdownMenu>
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageUploadButton text="" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <button
          onClick={() => {
            const url = prompt("URL do YouTube:");
            if (!url || !editor) return;

            if (typeof editor.commands.setYoutubeVideo !== "function") {
              console.warn("Youtube extension não registrada.");
              return;
            }

            editor
              .chain()
              .focus()
              .setYoutubeVideo({ src: url, width: 640, height: 360 })
              .run();
          }}
          className="tiptap-button"
          data-style="ghost"
        >
          <i className="fa-brands fa-youtube"></i>
        </button>

        <button
          onClick={() => {
            const url = prompt("URL do Loom:");
            if (!url || !editor) return;

            const embed = getLoomEmbedUrl(url);
            if (!embed) {
              alert("URL do Loom inválida");
              return;
            }

            editor
              .chain()
              .focus()
              .insertContent({
                type: "loom",
                attrs: {
                  src: embed,
                  width: 640,
                  height: 360,
                },
              })
              .run();
          }}
          className="tiptap-button"
          data-style="ghost"
        >
          <i className="fa-solid fa-video"></i>
        </button>

        <DropdownMenu
          open={ytDisabled ? false : ytMenuOpen}
          onOpenChange={(open) => {
            if (ytDisabled) return;
            setYtMenuOpen(open);
            if (open) syncWidthFromNode();
          }}
        >
          {ytDisabled ? (
            <Button
              type="button"
              data-style="ghost"
              disabled
              aria-label="Tamanho do vídeo"
              title="Tamanho do vídeo"
            >
              <i className="fa-solid fa-up-right-and-down-left-from-center text-xxs mt-1"></i>
              <ChevronDownIcon className="tiptap-button-dropdown-small" />
            </Button>
          ) : (
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                data-style="ghost"
                aria-label="Tamanho do vídeo"
                title="Tamanho do vídeo"
              >
                <i className="fa-solid fa-up-right-and-down-left-from-center text-xxs mt-1"></i>
                <ChevronDownIcon className="tiptap-button-dropdown-small" />
              </Button>
            </DropdownMenuTrigger>
          )}

          <DropdownMenuContent
            align="start"
            portal={isMobile}
            style={{ background: "#f5f5f5", borderRadius: 4 }}
          >
            <div style={{ width: 260, padding: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 5,
                }}
              >
                <span>Largura</span>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 0 }}>
                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={`${ytWidth === 320 ? "tiptap-button-active" : ""}`}
                  onClick={() => {
                    setYtWidth(320);
                    setYoutubeSize(editor, 320);
                  }}
                >
                  320
                </Button>
                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={`${ytWidth === 480 ? "tiptap-button-active" : ""}`}
                  onClick={() => {
                    setYtWidth(480);
                    setYoutubeSize(editor, 480);
                  }}
                >
                  480
                </Button>
                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={`${ytWidth === 640 ? "tiptap-button-active" : ""}`}
                  onClick={() => {
                    setYtWidth(640);
                    setYoutubeSize(editor, 640);
                  }}
                >
                  640
                </Button>
                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={`${ytWidth === 720 ? "tiptap-button-active" : ""}`}
                  onClick={() => {
                    setYtWidth(720);
                    setYoutubeSize(editor, 720);
                  }}
                >
                  720
                </Button>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 8,
                  marginBottom: 5,
                }}
              >
                <span>Alinhamento</span>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 0 }}>
                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={
                    editor?.getAttributes("youtube")?.align === "left"
                      ? "tiptap-button-active"
                      : ""
                  }
                  onClick={() =>
                    editor?.chain().focus().setYoutubeAlign("left").run()
                  }
                  title="Alinhar à esquerda"
                >
                  <AlignLeftIcon className="tiptap-button-icon" />
                </Button>

                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={
                    editor?.getAttributes("youtube")?.align === "center"
                      ? "tiptap-button-active"
                      : ""
                  }
                  onClick={() =>
                    editor?.chain().focus().setYoutubeAlign("center").run()
                  }
                  title="Centralizar"
                >
                  <AlignCenterIcon className="tiptap-button-icon" />
                </Button>

                <Button
                  data-style="ghost"
                  disabled={!editor?.isActive("youtube")}
                  className={
                    editor?.getAttributes("youtube")?.align === "right"
                      ? "tiptap-button-active"
                      : ""
                  }
                  onClick={() =>
                    editor?.chain().focus().setYoutubeAlign("right").run()
                  }
                  title="Alinhar à direita"
                >
                  <AlignRightIcon className="tiptap-button-icon" />
                </Button>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          onClick={() => {
            handlePreview();
            editor.chain().focus().run();
          }}
          className="tiptap-button"
          data-style="ghost"
          data-active-state={isPreview ? "on" : "off"}
        >
          <BvIcon
            width={18}
            height={18}
            name="importContacts"
            variant="solid"
            weight={600}
            style={{ color: isPreview ? "#6229ff" : "#55565c" }}
          />
        </button>

        <button
          type="button"
          className="tiptap-button"
          data-style="ghost"
          onClick={() => fileRef.current?.click()}
        >
          <i className="fa-solid fa-file-excel"></i>
        </button>

        <input
          type="file"
          accept="image/*,.xls,.xlsx,.xlsm,.rar,.zip"
          hidden
          ref={fileRef}
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];

            if (!file || !editor) return;

            if (file.type.startsWith("image/")) {
              await insertInlineImage(file);
              input.value = "";
              return;
            }

            const parsedExcel = await parseExcel(file);

            editor
              .chain()
              .focus()
              .insertContent({
                type: "excelTable",
                attrs: {
                  data: JSON.stringify(parsedExcel),
                },
              })
              .run();

            input.value = "";
          }}
        />

        <button
          className="tiptap-button"
          data-style="ghost"
          onClick={() => {
            editor?.commands.insertContent({
              type: "htmlPreview",
              attrs: {
                code: "<html>\n  <head>\n    <style></style>\n  </head>\n  <body>\n    <script></script>\n  </body>\n</html>",
              },
            });
          }}
        >
          <i className="fa-solid fa-file-code"></i>
        </button>

        <input
          type="file"
          accept="image/*,.xls,.xlsx,.xlsm,.rar,.zip"
          hidden
          ref={imageFileRef}
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];

            if (!file || !editor) return;

            if (file.type.startsWith("image/")) {
              await insertInlineImage(file);
              input.value = "";
              return;
            }

            const parsedExcel = await parseExcel(file);

            editor
              .chain()
              .focus()
              .insertContent({
                type: "excelTable",
                attrs: {
                  data: JSON.stringify(parsedExcel),
                },
              })
              .run();

            input.value = "";
          }}
        />

        <button
          type="button"
          className="tiptap-button"
          data-style="ghost"
          onClick={() => imageFileRef.current?.click()}
        >
          <img src={imageLine} />
        </button>
      </ToolbarGroup>

      {/* <ToolbarSeparator /> */}

      <Spacer />

      {/* {isMobile && <ToolbarSeparator />} */}

      {/* <ToolbarGroup>
        <ThemeToggle />
      </ToolbarGroup> */}
    </>
  );
};

const CompactToolbarContent = ({ editor }: { editor: any }) => {
  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <LinkRemoveButton editor={editor} />
        <LinkAddButton editor={editor} />
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="underline" />
      </ToolbarGroup>

      <Spacer />
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
  editor,
}: {
  type: "highlighter" | "link" | "color";
  onBack: () => void;
  editor: any;
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : type === "color" ? (
          <i className="fa-solid fa-font tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator style={{ marginRight: "8px" }} />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : type === "color" ? (
      <ColorFontPopoverContent editor={editor} key="color-mobile" />
    ) : (
      <LinkContent />
    )}
  </>
);

export function SimpleEditor({
  content = "",
  viewToolbar = "full",
  onChange,
}: {
  content?: string;
  viewToolbar?: "full" | "compact" | "none";
  onChange?: (value: { html: string; json: unknown }) => void;
}) {
  const isMobile = useIsMobile();
  const { height } = useWindowSize();
  const [mobileView, setMobileView] = React.useState<
    "main" | "highlighter" | "link" | "color"
  >("main");
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        horizontalRule: false,
        link: {
          autolink: viewToolbar !== "compact",
          linkOnPaste: viewToolbar !== "compact",
          openOnClick: false,
          protocols: ["http", "https", "mailto"],
        },
      }),
      HtmlPreviewNode,
      LoomVideo,
      LoomAlign.configure({
        width: 640,
        height: 360,
        modestBranding: true,
        HTMLAttributes: {
          class: "tt-loom",
        },
      }),
      AlignableImage.configure({
        HTMLAttributes: { class: "tt-resizable-image" },
      }),
      ResizableImage.configure({
        HTMLAttributes: { class: "tt-resizable-image" },
      }),
      FileAttachment,
      ExcelTableNode,
      HorizontalRule,
      TextAlign.configure({
        types: ["heading", "paragraph", "image", "resizableImage"],
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      TextStyleWithColor,
      // Image,
      Typography,
      Superscript,
      Subscript,
      Selection,
      InlineImage,
      // ImageUploadNode.configure({
      //   accept: "image/*, application/pdf, .pdf",
      //   maxSize: MAX_FILE_SIZE,
      //   limit: 1,
      //   upload: handleImageUpload,
      //   onError: (error: any) => console.error("Upload failed:", error),
      // }),
      ImageUploadNode.configure({
        accept: "image/*,application/pdf,.pdf,.csv,.xls,.xlsx,.xlsm,.zip,.rar",
        maxSize: MAX_FILE_SIZE,
        limit: 1,
        upload: handleImageUpload,
        type: "resizableImage",
      }),
      Youtube.configure({
        width: 640,
        height: 360,
        modestBranding: true,
        HTMLAttributes: {
          class: "tt-youtube",
        },
      }),
      YoutubeAlign,
      ResponsiveTable.configure({
        resizable: true,
        lastColumnResizable: true,
        cellMinWidth: 90,
      }),
      TableRow,
      TableCellWithBg,
      FreezeTableCommand,
      CustomTableHeader,
    ],
    content,
    onCreate: ({ editor }) => {
      onChange?.({ html: editor.getHTML(), json: editor.getJSON() });
    },
    onUpdate: ({ editor }) => {
      onChange?.({ html: editor.getHTML(), json: editor.getJSON() });
    },
  });

  React.useEffect(() => {
    if (editor && typeof content === "string" && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  React.useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main");
    }
  }, [isMobile, mobileView]);

  return (
    <div className="simple-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        <div className="simple-editor-scroll">
          {viewToolbar !== "none" && (
            <Toolbar
              ref={toolbarRef}
              style={{
                ...(isMobile
                  ? {
                      bottom: `calc(100% - ${height - rect.y}px)`,
                    }
                  : {}),
              }}
              className="tiptap-toolbar sticky-top"
            >
              {viewToolbar === "compact" ? (
                <CompactToolbarContent editor={editor} />
              ) : mobileView === "main" ? (
                <MainToolbarContent
                  onHighlighterClick={() => setMobileView("highlighter")}
                  onLinkClick={() => setMobileView("link")}
                  onColorClick={() => setMobileView("color")}
                  isMobile={isMobile}
                  editor={editor}
                />
              ) : (
                <MobileToolbarContent
                  type={mobileView === "highlighter" ? "highlighter" : mobileView === "link" ? "link" : "color"}
                  onBack={() => setMobileView("main")}
                  editor={editor}
                />
              )}
            </Toolbar>
          )}
          <EditorContent
            editor={editor}
            role="presentation"
            className="simple-editor-content"
          />

          {editor && <TableEdgeControls editor={editor} />}
        </div>
      </EditorContext.Provider>
    </div>
  );
}
